import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Alert, Snackbar } from '@mui/material';

const FeedbackContext = createContext(null);

export function FeedbackProvider({ children }) {
  const [state, setState] = useState({
    open: false,
    message: '',
    severity: 'info',
  });

  const showFeedback = useCallback((message, severity = 'info') => {
    if (!message) return;
    setState({ open: true, message, severity });
  }, []);

  const showSuccess = useCallback(
    (message) => showFeedback(message, 'success'),
    [showFeedback],
  );
  const showError = useCallback(
    (message) => showFeedback(message, 'error'),
    [showFeedback],
  );

  const handleClose = useCallback((_, reason) => {
    if (reason === 'clickaway') {
      setState((prev) => ({ ...prev, open: false }));
      return;
    }
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  const value = useMemo(
    () => ({ showFeedback, showSuccess, showError }),
    [showFeedback, showSuccess, showError],
  );

  return (
    <FeedbackContext.Provider value={value}>
      {children}
      <Snackbar
        open={state.open}
        autoHideDuration={4500}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleClose}
          severity={state.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {state.message}
        </Alert>
      </Snackbar>
    </FeedbackContext.Provider>
  );
}

export function useFeedback() {
  const ctx = useContext(FeedbackContext);
  if (!ctx) {
    return {
      showFeedback: () => {},
      showSuccess: () => {},
      showError: () => {},
    };
  }
  return ctx;
}
